import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, GitBranch, GitPullRequest } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill, type DeploymentStatus } from "@/components/StatusPill";
import { CommitRef } from "@/components/CommitRef";
import { RelativeTime } from "@/components/RelativeTime";

interface DeploymentRow {
  id: string;
  status: DeploymentStatus;
  env: "production" | "staging" | "preview" | "development";
  branch: string;
  commitSha: string;
  createdAt: string;
  readyAt?: string | null;
  durationMs?: number | null;
}

interface PrCommentRow {
  prNumber: number;
  deploymentId: string;
  updatedAt: string;
}

export function Previews() {
  const { org, project } = useParams<{ org: string; project: string }>();

  // Reuse the deployments endpoint filtered to env=preview. The list view
  // groups them by branch so the user can see "what's the latest preview
  // of feature-x?" at a glance.
  const deployments = useQuery({
    queryKey: ["preview-deployments", org, project],
    queryFn: async () => {
      const r = await api.get<{ deployments: DeploymentRow[] }>(
        `/orgs/${org}/projects/${project}/deployments?env=preview&limit=100`,
      );
      return r.deployments;
    },
  });

  const prComments = useQuery({
    queryKey: ["pr-comments", org, project],
    queryFn: async () => {
      const r = await api
        .get<{ comments: PrCommentRow[] }>(`/orgs/${org}/projects/${project}/pr-comments`)
        .catch(() => ({ comments: [] as PrCommentRow[] }));
      return r.comments;
    },
  });

  const byBranch = groupByBranch(deployments.data ?? []);
  const prByDeployment = new Map(
    (prComments.data ?? []).map((c) => [c.deploymentId, c.prNumber]),
  );

  return (
    <div className="space-y-4">
      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
            <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
            Branch &amp; PR previews
          </h3>
          <span className="text-2xs text-subtle-foreground">
            {Object.keys(byBranch).length} active branch{Object.keys(byBranch).length === 1 ? "" : "es"}
          </span>
        </div>
        {deployments.isLoading && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {!deployments.isLoading && Object.keys(byBranch).length === 0 && (
          <div className="px-4 py-12 text-center">
            <GitBranch className="h-6 w-6 text-muted-foreground/40 mx-auto" />
            <div className="text-sm text-muted-foreground mt-3">No preview deployments yet</div>
            <div className="text-xs text-subtle-foreground mt-1">
              Push to a branch or open a PR — a preview URL will land here.
            </div>
          </div>
        )}
        {Object.entries(byBranch).map(([branch, list]) => {
          const latest = list[0]!;
          const prNumber = prByDeployment.get(latest.id);
          const previewUrl = previewUrlFor(latest.branch, project!, org!);
          return (
            <div key={branch} className="of-row flex-wrap items-start gap-3 py-3">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  {prNumber ? (
                    <GitPullRequest className="h-3.5 w-3.5 text-done" />
                  ) : (
                    <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <span className="font-mono text-sm font-semibold min-w-0 truncate">{branch}</span>
                  {prNumber && (
                    <span className="text-2xs font-mono bg-done-subtle text-done border border-done/30 px-1.5 py-0.5 rounded-sm">
                      PR #{prNumber}
                    </span>
                  )}
                  <span className="text-2xs text-subtle-foreground">
                    {list.length} deployment{list.length === 1 ? "" : "s"}
                  </span>
                </div>
                <CommitRef branch={branch} sha={latest.commitSha} />
                <div className="flex items-center gap-2 min-w-0">
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-mono text-accent hover:underline inline-flex items-center gap-1 min-w-0 max-w-full break-all"
                  >
                    {previewUrl.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <StatusPill status={latest.status} />
                <RelativeTime value={latest.readyAt ?? latest.createdAt} />
                <Link
                  to={`/orgs/${org}/projects/${project}/deployments/${latest.id}`}
                  className="text-xs text-accent hover:underline"
                >
                  Logs →
                </Link>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-2xs text-subtle-foreground px-1">
        Per-branch retention: latest 3 successful deployments kept. PR-closed previews tear down + artifact GC at +7 days.
      </p>
    </div>
  );
}

function groupByBranch(deps: DeploymentRow[]): Record<string, DeploymentRow[]> {
  const out: Record<string, DeploymentRow[]> = {};
  // Sort newest first; group entries naturally land newest at index 0.
  const sorted = [...deps].sort((a, b) =>
    (b.readyAt ?? b.createdAt).localeCompare(a.readyAt ?? a.createdAt),
  );
  for (const d of sorted) {
    if (!out[d.branch]) out[d.branch] = [];
    out[d.branch]!.push(d);
  }
  return out;
}

function previewUrlFor(branch: string, project: string, org: string): string {
  const slug = branch.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  const apex = import.meta.env.VITE_PLATFORM_APEX ?? window.location.host.split(":")[0];
  return `https://${slug}--${project}.${org}.${apex}`;
}
